import { useCallback, useEffect } from 'react';
import { shuffleList } from '@/modules/gameUtils/random';

export default function useConceptRaceGameloop({
  onAnswer,
  onFinish,
  game,
  mode,
}) {
  // Initialize game and first concept.
  useEffect(() => {
    const concepts = shuffleList(game.state.gameConf.animations);
    const leftConcepts = [...concepts];
    const currentConcept = getConceptWithRandomOptions(leftConcepts.shift());
    game.actions.setState({
      currentConcept,
      concepts,
      leftConcepts,
      gameConf: { ...game.state.gameConf, mode },
      lastCorrectId: null,
    });
  }, []);

  // Detect user answer or time out and activate feedback
  useEffect(() => {
    if (
      !game.state.givingFeedback &&
      (game.state.currentAnswerId || game.state.timedOut)
    ) {
      updateAnswersLog();

      game.actions.setState({
        givingFeedback: true,
      });
    }
  }, [
    game.state.givingFeedback,
    game.state.currentAnswerId,
    game.state.timedOut,
    updateAnswersLog,
  ]);

  //Change concept after answer or timeout feedback
  useEffect(() => {
    if (game.state.feedbackFinished) {
      changeConcept();
    }
  }, [
    game.state.feedbackFinished,
    game.state.currentAnswerId,
    game.state.timedOut,
    changeConcept,
  ]);

  const changeConcept = useCallback(() => {
    const currentConcept = getNextConceptOrFinish(game.state.leftConcepts);
    game.actions.setState({
      currentConcept,
      timedOut: false,
      feedbackFinished: false,
      givingFeedback: false,
      currentAnswerId: null,
      progressSent: false,
    });
  }, [game.state.leftConcepts]);

  const updateAnswersLog = useCallback(() => {
    const isRightAnswer =
      game.state.currentAnswerId == game.state.currentConcept.id;
    const answersLog = [
      ...game.state.answersLog,
      {
        conceptId: game.state.currentConcept.id,
        answerId: game.state.currentAnswerId,
        correct: isRightAnswer,
        timedOut: game.state.timedOut,

        formattedAnswer: {
          id: game.state.currentConcept.id, // id of the concept that was asked
          answer: game.state.currentConcept.text, // text of the concept that was asked
          answerId: game.state.currentAnswerId, // id of the answer that was selected
          rigthAnswer: [
            game.state.currentConcept.answerOptions.find(
              (answer) => answer.id == game.state.currentAnswerId
            )?.text,
          ], // text of the concept that was asked
          correct: isRightAnswer,
          timedOut: game.state.timedOut,
        },
      },
    ];

    let leftConcepts = game.state.leftConcepts;
    let secondChanceConcepts = game.state.secondChanceConcepts;

    if (!isRightAnswer) {
      [leftConcepts, secondChanceConcepts] = getUpdatedLeftConcepts({
        currentConcept: game.state.currentConcept,
        leftConcepts,
        secondChanceConcepts,
      });
    } else {
      secondChanceConcepts = secondChanceConcepts.filter(
        (conceptId) => conceptId != game.state.currentConcept.id
      );
    }
    onAnswer({
      gameConf: game.state.gameConf,
      answers: answersLog,
      secondChanceConcepts,
    });

    game.actions.setState({
      answersLog,
      leftConcepts,
      secondChanceConcepts,
      lastCorrectId: isRightAnswer ? game.state.currentConcept.id : null,
    });
  }, [
    game.state.currentConcept,
    game.state.currentAnswerId,
    game.state.timedOut,
    game.state.answersLog,
  ]);

  const getNextConceptOrFinish = useCallback((leftConcepts) => {
    if (leftConcepts.length) {
      const currentConcept = getConceptWithRandomOptions(leftConcepts.shift());
      return currentConcept;
    } else {
      onFinish();
      return null;
    }
  }, []);

  const getConceptWithRandomOptions = useCallback(
    (concept) => {
      const randomWrongOptionConcepts = shuffleList(
        game.state.gameConf.animations
      )
        .filter((c) => c.id != concept.id)
        .slice(0, 2);

      const randomWrongOptions = randomWrongOptionConcepts.map((c) => {
        return {
          id: c.id,
          text: c.text,
        };
      });

      const rightAnswer = {
        id: concept.id,
        text: concept.text,
      };

      const answerOptions = shuffleList([rightAnswer, ...randomWrongOptions]);
      return { ...concept, answerOptions, rightAnswer };
    },
    [game.state.gameConf.animations]
  );

  const getUpdatedLeftConcepts = useCallback(
    ({ currentConcept, leftConcepts, secondChanceConcepts }) => {
      if (!secondChanceConcepts.includes(currentConcept.id)) {
        secondChanceConcepts = [...secondChanceConcepts, currentConcept.id];
        leftConcepts = [...leftConcepts, currentConcept];
      }
      return [leftConcepts, secondChanceConcepts];
    },
    [game.state.currentAnswerId]
  );
}
